import { z } from "zod";

/**
 * The 6 canonical Mars Men personas. Mirrors mars-men-lp-generator/static/audience_personas.md.
 * A quiz classifier MUST return one of these keys.
 */
export const PERSONA_KEYS = [
  "trt-alternative",
  "aging-men",
  "skeptical-buyer",
  "everyday-man",
  "performance",
  "glp1-user",
] as const;

export const PersonaKeySchema = z.enum(PERSONA_KEYS);
export type PersonaKey = z.infer<typeof PersonaKeySchema>;

export const AnswerValueSchema = z.union([
  z.string(),
  z.array(z.string()),
  z.number(),
  z.null(),
]);
export type AnswerValue = z.infer<typeof AnswerValueSchema>;

export const AnswersSchema = z.record(z.string(), AnswerValueSchema);
export type Answers = z.infer<typeof AnswersSchema>;

const OptionSchema = z.object({
  value: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9_\-]*$/, "option id must be lowercase, may contain digits/_/-, no leading _, no '+'")
    .refine((v) => v !== "advance", "'advance' is reserved for info-screen telemetry"),
  label: z.string().min(1),
  emoji: z.string().optional(),
});
export type QuestionOption = z.infer<typeof OptionSchema>;

const WhyWeAskSchema = z.object({
  heading: z.string().default("Why we ask"),
  body: z.string().min(1),
});
export type WhyWeAsk = z.infer<typeof WhyWeAskSchema>;

export const VariantTagSchema = z.enum(["control", "variant"]);
export type VariantTag = z.infer<typeof VariantTagSchema>;

const SingleQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("single"),
  prompt: z.string().min(1),
  helper: z.string().optional(),
  options: z.array(OptionSchema).min(2),
  whyWeAsk: WhyWeAskSchema.optional(),
  variantTag: VariantTagSchema.optional(),
});

const MultiQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("multi"),
  prompt: z.string().min(1),
  helper: z.string().optional(),
  options: z.array(OptionSchema).min(2),
  min: z.number().int().min(0).optional(),
  max: z.number().int().min(1).optional(),
  whyWeAsk: WhyWeAskSchema.optional(),
  variantTag: VariantTagSchema.optional(),
});

const SliderQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("slider"),
  prompt: z.string().min(1),
  helper: z.string().optional(),
  min: z.number(),
  max: z.number(),
  step: z.number().optional(),
  minLabel: z.string().optional(),
  maxLabel: z.string().optional(),
  whyWeAsk: WhyWeAskSchema.optional(),
  variantTag: VariantTagSchema.optional(),
});

const TextQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("text"),
  prompt: z.string().min(1),
  helper: z.string().optional(),
  placeholder: z.string().optional(),
  whyWeAsk: WhyWeAskSchema.optional(),
  variantTag: VariantTagSchema.optional(),
});

/**
 * Numeric input question for calculator-style quizzes. Stores the answer as a
 * `number` in `answers[id]`, or `null` when the question is `optional` and the
 * user skipped it. Cross-field constraints (min < max, defaultValue in range)
 * are enforced by `QuizDefinitionSchema.superRefine` — `discriminatedUnion`
 * requires raw ZodObjects so the refinements live one level up.
 */
const NumberQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("number"),
  prompt: z.string().min(1),
  helper: z.string().optional(),
  unit: z.string().optional(),
  placeholder: z.string().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  step: z.number().positive().optional(),
  defaultValue: z.number().optional(),
  optional: z.boolean().optional(),
  whyWeAsk: WhyWeAskSchema.optional(),
  variantTag: VariantTagSchema.optional(),
});

/**
 * Info screen: educational interstitial between questions. Renders rich content
 * with a Continue button. Stores `null` in answers so it doesn't influence the
 * classifier. Use to build conviction between answer pages.
 */
const InfoQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("info"),
  heading: z.string().min(1),
  body: z.array(z.string().min(1)).min(1),
  image: z
    .object({
      src: z.string().min(1),
      alt: z.string().default(""),
    })
    .optional(),
  animation: z.enum(["analyzing"]).optional(),
  autoAdvanceMs: z.number().int().min(0).optional(),
  cta: z.string().optional(),
  variantTag: VariantTagSchema.optional(),
});

export const QuestionSchema = z.discriminatedUnion("type", [
  SingleQuestionSchema,
  MultiQuestionSchema,
  SliderQuestionSchema,
  TextQuestionSchema,
  NumberQuestionSchema,
  InfoQuestionSchema,
]);
export type Question = z.infer<typeof QuestionSchema>;

export const CaptureConfigSchema = z.object({
  fields: z.array(z.enum(["email", "phone"])).min(1),
  required: z.array(z.enum(["email", "phone"])).default([]),
  headline: z.string().optional(),
  subheadline: z.string().optional(),
  cta: z.string().optional(),
  disclaimer: z.string().optional(),
});
export type CaptureConfig = z.infer<typeof CaptureConfigSchema>;

/**
 * Quiz kind discriminator. `persona` quizzes route through `classifier` to one
 * of the 6 Mars Men personas and render a persona narrative. `calculator`
 * quizzes route through `compute` to a deterministic numeric result and render
 * a calculator-result surface (hero value, status pill, optional breakdown,
 * methodology). New kinds require a corresponding state-machine branch and
 * result component; do not add one without updating both.
 */
export const QUIZ_KINDS = ["persona", "calculator"] as const;
export const QuizKindSchema = z.enum(QUIZ_KINDS);
export type QuizKind = z.infer<typeof QuizKindSchema>;

/**
 * CalculatorResult is the runtime output of a calculator quiz's `compute`
 * function. It is the calculator-mode analog of `TSuppressionProfile`: pure
 * function of `answers`, reproducible server-side from the persisted
 * submission. UI surfaces (hero, status, secondary values, breakdown) are
 * data-driven so a single component renders every calculator quiz.
 */
const CalculatorValueSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  value: z.number(),
  unit: z.string().min(1),
  altUnit: z
    .object({
      value: z.number(),
      unit: z.string().min(1),
    })
    .optional(),
  status: z.enum(["low", "normal", "high"]).optional(),
  referenceRange: z.string().optional(),
});
export type CalculatorValue = z.infer<typeof CalculatorValueSchema>;

const CalculatorBreakdownSegmentSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  pct: z.number().min(0).max(100),
});
export type CalculatorBreakdownSegment = z.infer<typeof CalculatorBreakdownSegmentSchema>;

const CalculatorBreakdownSchema = z.object({
  title: z.string().min(1),
  segments: z.array(CalculatorBreakdownSegmentSchema).min(2),
});
export type CalculatorBreakdown = z.infer<typeof CalculatorBreakdownSchema>;

const CalculatorStatusSchema = z.object({
  key: z.enum(["low", "normal", "high"]),
  label: z.string().min(1),
  message: z.string().min(1),
});
export type CalculatorStatus = z.infer<typeof CalculatorStatusSchema>;

/**
 * Reference table row -- mirrors the "Reference Ranges" table on the original
 * standalone calculator (metric / your value / normal range / status).
 */
const CalculatorReferenceRowSchema = z.object({
  metric: z.string().min(1),
  value: z.string().min(1),
  range: z.string().min(1),
  status: z.enum(["low", "normal", "high"]),
});
export type CalculatorReferenceRow = z.infer<typeof CalculatorReferenceRowSchema>;

/**
 * One row on the "What This Means" page -- a linear scale visualization that
 * positions a value along a Low / Normal / High band with labeled tick marks.
 */
const CalculatorScaleSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  value: z.number(),
  unit: z.string().min(1),
  status: z.enum(["low", "normal", "high"]),
  min: z.number(),
  max: z.number(),
  lowMax: z.number(),
  highMin: z.number(),
  lowCopy: z.string().optional(),
  normalCopy: z.string().optional(),
  highCopy: z.string().optional(),
});
export type CalculatorScale = z.infer<typeof CalculatorScaleSchema>;

/**
 * Interpretation card on the "What This Means" page. The `accent` controls the
 * left-border color and matches a status/semantic vocabulary so the page reads
 * as a quick at-a-glance summary.
 */
const CalculatorInterpretationCardSchema = z.object({
  key: z.string().min(1),
  emoji: z.string().min(1),
  title: z.string().min(1),
  body: z.string().min(1),
  accent: z.enum(["green", "orange", "red", "blue"]),
});
export type CalculatorInterpretationCard = z.infer<typeof CalculatorInterpretationCardSchema>;

const CalculatorNextStepSchema = z.object({
  emoji: z.string().min(1),
  title: z.string().min(1),
  body: z.string().min(1),
});
export type CalculatorNextStep = z.infer<typeof CalculatorNextStepSchema>;

const CalculatorInterpretationSchema = z.object({
  scales: z.array(CalculatorScaleSchema).optional(),
  cards: z.array(CalculatorInterpretationCardSchema).min(1),
  nextSteps: z
    .object({
      title: z.string().min(1),
      items: z.array(CalculatorNextStepSchema).min(1),
    })
    .optional(),
});
export type CalculatorInterpretation = z.infer<typeof CalculatorInterpretationSchema>;

const CalculatorRecommendationLevelSchema = z.object({
  eyebrow: z.string().min(1),
  title: z.string().min(1),
  recommended: z.boolean().optional(),
});
export type CalculatorRecommendationLevel = z.infer<typeof CalculatorRecommendationLevelSchema>;

const CalculatorRecommendationSchema = z.object({
  headline: z.string().min(1),
  headlineAccent: z.string().optional(),
  body: z.string().min(1),
  levels: z.array(CalculatorRecommendationLevelSchema).min(1),
  cta: z.object({
    label: z.string().min(1),
    href: z.string().min(1),
  }),
});
export type CalculatorRecommendation = z.infer<typeof CalculatorRecommendationSchema>;

const CalculatorBannerSchema = z.object({
  emoji: z.string().min(1),
  title: z.string().min(1),
  body: z.string().min(1),
});
export type CalculatorBanner = z.infer<typeof CalculatorBannerSchema>;

export const CalculatorResultSchema = z.object({
  hero: CalculatorValueSchema,
  status: CalculatorStatusSchema,
  banner: CalculatorBannerSchema.optional(),
  secondaryValues: z.array(CalculatorValueSchema).optional(),
  breakdown: CalculatorBreakdownSchema.optional(),
  referenceTable: z.array(CalculatorReferenceRowSchema).optional(),
  interpretation: CalculatorInterpretationSchema.optional(),
  recommendation: CalculatorRecommendationSchema.optional(),
  methodology: z.string().optional(),
  disclaimer: z.string().optional(),
});
export type CalculatorResult = z.infer<typeof CalculatorResultSchema>;

export const ExperimentSchema = z.object({
  intelligemsTestId: z.string().min(8).max(64),
  // Friendly name surfaced in the admin status bar so operators see what's
  // running without translating UUIDs. Format convention: "MM-0067 Change
  // First Page" — Intelligems ticket id + short description.
  name: z.string().min(1).max(120),
});
export type Experiment = z.infer<typeof ExperimentSchema>;

/**
 * Quiz definition. Branching and classifier are pure functions of `answers`.
 *
 * - branching(answers, currentQuestionId): the id of the next question, or null
 *   if the questions phase is complete (advances to the email gate).
 * - classifier(answers): the persona key to assign to this submission.
 */
export const QuizTestimonialSchema = z.object({
  quote: z.string().min(1),
  author: z.string().min(1),
  avatar: z.string().optional(),
  variantTag: VariantTagSchema.optional(),
});
export type QuizTestimonial = z.infer<typeof QuizTestimonialSchema>;

export const QuizMetaSchema = z.object({
  title: z.string().min(1),
  introHeadline: z.string().min(1),
  // Optional accent word rendered in mars-orange inside the (CSS-uppercased)
  // intro H1. QuizIntro defaults to "really" when omitted, so existing quizzes
  // (ignite/quiz-v7/cortisol) are byte-identical. quiz-trt-switch sets "stuck".
  introAccent: z.string().min(1).max(40).optional(),
  introBody: z.string().optional(),
  testimonial: QuizTestimonialSchema.optional(),
});
export type QuizMeta = z.infer<typeof QuizMetaSchema>;

export const QuizDefinitionSchema = z
  .object({
    id: z.string().min(1),
    slug: z.string().min(1).max(64).regex(/^[a-z0-9-]+$/),
    kind: QuizKindSchema.optional(),
    meta: QuizMetaSchema,
    questions: z.array(QuestionSchema).min(1),
    experiment: ExperimentSchema.optional(),
    capture: CaptureConfigSchema.optional(),
  })
  .superRefine((quiz, ctx) => {
    quiz.questions.forEach((q, qIdx) => {
      if (q.type !== "number") return;
      if (q.min !== undefined && q.max !== undefined && q.min >= q.max) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "min must be strictly less than max",
          path: ["questions", qIdx, "min"],
        });
      }
      if (q.defaultValue !== undefined) {
        if (q.min !== undefined && q.defaultValue < q.min) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "defaultValue must be >= min",
            path: ["questions", qIdx, "defaultValue"],
          });
        }
        if (q.max !== undefined && q.defaultValue > q.max) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "defaultValue must be <= max",
            path: ["questions", qIdx, "defaultValue"],
          });
        }
      }
    });
  });

export type QuizDefinition = z.infer<typeof QuizDefinitionSchema>;

/**
 * T-suppression profile dimensions. Derived from `answers` via
 * `quiz.deriveProfile(answers)` and rendered on the result page above the
 * persona-static hero/education content. The profile is the "We analyzed your X"
 * personalized-plan layer; the persona content is the UMP -> UMS bridge.
 *
 * Source of truth for the underlying medicine: `docs/t-suppression-research.md`.
 * Severity scale uses HIGH = highly suppressed (i.e. "level of T suppression"),
 * not high T -- match the meter on the result-page mock.
 */
export const SUPPRESSION_LEVELS = ["low", "normal", "medium", "high"] as const;
export const SuppressionLevelSchema = z.enum(SUPPRESSION_LEVELS);
export type SuppressionLevel = z.infer<typeof SuppressionLevelSchema>;

export const DECLINE_PATTERNS = [
  "stable",
  "early-onset",
  "acute",
  "progressive",
  "stalled-recovery",
] as const;
export const DeclinePatternSchema = z.enum(DECLINE_PATTERNS);
export type DeclinePattern = z.infer<typeof DeclinePatternSchema>;

export const ROOT_CAUSES = [
  "cortisol-dominance",
  "chronic-stress",
  "sleep-deprivation",
  "visceral-adiposity",
  "metabolic-syndrome",
  "lifestyle-load",
  "age-related-decline",
  "overtraining",
  "endocrine-disruption",
  "catabolic-deficit",
  "blocked-testosterone",
  // Forced-only cause for the quiz-trt-switch quiz: the locked meter reads as
  // endogenous production, which exogenous TRT suppresses. Omitted from
  // ROOT_CAUSE_ORDER / scoreRootCauses in ignite-profile.ts so it is never
  // scored organically — it surfaces ONLY via forcePrimaryCause.
  "exogenous-suppression",
] as const;
export const RootCauseSchema = z.enum(ROOT_CAUSES);
export type RootCause = z.infer<typeof RootCauseSchema>;

export const QOL_IMPACT_LEVELS = ["low", "medium", "high"] as const;
export const QolImpactSchema = z.enum(QOL_IMPACT_LEVELS);
export type QolImpact = z.infer<typeof QolImpactSchema>;

/**
 * Evidence chips surface the user's actual quiz answers under the meter so the
 * profile reads as "we used your data" rather than a generic verdict. Each
 * item is a (label, value) pair rendered as a small pill.
 */
export const EvidenceItemSchema = z.object({
  label: z.string().min(1),
  value: z.string().min(1),
});
export type EvidenceItem = z.infer<typeof EvidenceItemSchema>;

/**
 * Personalized "what the user wants from this protocol" pair, derived from the
 * results-want question. Surfaces under the assessment card so every collected
 * answer pays back personalization on the result page.
 */
export const ExpectedOutcomeSchema = z.object({
  label: z.string().min(1),
  copy: z.string().min(1),
});
export type ExpectedOutcome = z.infer<typeof ExpectedOutcomeSchema>;

export const CONCERN_KEYS = ["energy", "muscle", "sex-drive", "focus", "weight-loss"] as const;
export const ConcernKeySchema = z.enum(CONCERN_KEYS);
export type ConcernKey = z.infer<typeof ConcernKeySchema>;

export const TSuppressionProfileSchema = z.object({
  suppressionLevel: SuppressionLevelSchema,
  assessment: z.object({
    label: z.string().min(1),
    headline: z.string().min(1),
    explanation: z.string().min(1),
  }),
  declinePattern: DeclinePatternSchema,
  rootCauses: z.array(RootCauseSchema).min(2).max(3),
  qualityOfLifeImpact: QolImpactSchema,
  evidence: z.array(EvidenceItemSchema).min(2).max(6),
  expectedOutcome: ExpectedOutcomeSchema.optional(),
  cohort: ConcernKeySchema.optional(),
});
export type TSuppressionProfile = z.infer<typeof TSuppressionProfileSchema>;

export type PersonaQuiz = QuizDefinition & {
  kind?: "persona";
  branching: (answers: Answers, currentQuestionId: string) => string | null;
  classifier: (answers: Answers) => PersonaKey;
  deriveProfile?: (answers: Answers) => TSuppressionProfile;
};

export const CALCULATOR_LAYOUTS = ["stepped", "single-page"] as const;
export type CalculatorLayout = (typeof CALCULATOR_LAYOUTS)[number];

export type CalculatorQuiz = QuizDefinition & {
  kind: "calculator";
  /**
   * Controls how `CalculatorQuizRunner` lays out the question phase:
   * - `"stepped"` (default): one number question per screen with a Continue
   *   button per question. Matches the first calculator (free-t).
   * - `"single-page"`: intro hero + all number questions stacked on one screen
   *   with a single Submit button. For short calculators (1-4 inputs) where
   *   the user already has the values in hand and stepping is friction.
   *   `branching` is still required and validated at boot, but is not invoked
   *   during single-page rendering -- the runner walks `quiz.questions` in
   *   array order. If a future single-page calculator needs conditional
   *   inputs, extend this layout's renderer first.
   */
  layout?: CalculatorLayout;
  /**
   * When true, the runner inserts a commitment loader and an email-capture
   * gate between compute and the result page. Submission is deferred until
   * the gate is filled (or explicitly skipped) so the captured email + the
   * implicit marketing-consent flag land on the `quiz_submissions` row that
   * triggers Klaviyo. When false/undefined, compute -> result is immediate
   * (the legacy free-t path; preserves back-compat with the first calculator
   * before the lead-capture surface was designed).
   */
  emailGate?: boolean;
  branching: (answers: Answers, currentQuestionId: string) => string | null;
  compute: (answers: Answers) => CalculatorResult;
};

export type Quiz = PersonaQuiz | CalculatorQuiz;

export function isCalculatorQuiz(quiz: Quiz): quiz is CalculatorQuiz {
  return quiz.kind === "calculator";
}

/**
 * Defines a persona-routing quiz with full type-checking. Use this in
 * `quizzes/<slug>.ts`. The first quiz, Ignite, was built with this helper.
 *
 * Example:
 *   export const igniteQuiz = defineQuiz({
 *     id: "ignite", slug: "ignite",
 *     meta: { ... },
 *     questions: [ ... ],
 *     capture: { ... },
 *     branching: (answers, current) => "next-id",
 *     classifier: (answers) => "trt-alternative",
 *   });
 */
export function defineQuiz(quiz: PersonaQuiz): PersonaQuiz {
  QuizDefinitionSchema.parse({
    id: quiz.id,
    slug: quiz.slug,
    kind: quiz.kind ?? "persona",
    meta: quiz.meta,
    questions: quiz.questions,
    experiment: quiz.experiment,
    capture: quiz.capture,
  });
  return { ...quiz, kind: "persona" };
}

/**
 * Defines a calculator-style quiz. The `compute` function is a pure function
 * of `answers` that produces a `CalculatorResult` — the calculator-mode analog
 * of `deriveProfile`. Same input -> same output, so the result is reproducible
 * server-side from the persisted submission.
 *
 * Calculator quizzes do not have a persona classifier or `deriveProfile`. They
 * render the calculator-result surface instead of the persona narrative.
 *
 * Example:
 *   export const freeTQuiz = defineCalculatorQuiz({
 *     id: "free-t", slug: "free-t",
 *     meta: { ... },
 *     questions: [ ... ],
 *     branching: (answers, current) => "next-id",
 *     compute: (answers) => ({ hero: ..., status: ..., ... }),
 *   });
 */
export function defineCalculatorQuiz(quiz: CalculatorQuiz): CalculatorQuiz {
  QuizDefinitionSchema.parse({
    id: quiz.id,
    slug: quiz.slug,
    kind: "calculator",
    meta: quiz.meta,
    questions: quiz.questions,
    experiment: quiz.experiment,
    capture: quiz.capture,
  });
  return { ...quiz, kind: "calculator" };
}
